import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Save, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AvatarPicker } from "@/components/avatar-picker";
import { PortalLayout } from "@/components/portal-layout";
import { useAuth, ROLE_HOME } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { updateProfile, updatePassword } from "@/lib/profile.functions";
import { avatarByKey, type AvatarKey } from "@/lib/avatars";
import { useEntitlements } from "@/hooks/use-entitlements";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
  head: () => ({
    meta: [
      { title: "Cài đặt tài khoản — Ezyclass" },
      { name: "description", content: "Cập nhật hồ sơ cá nhân, đổi mật khẩu, chọn ảnh đại diện và quản lý môn học giảng dạy trên tài khoản Ezyclass của bạn." },
      { property: "og:title", content: "Cài đặt tài khoản — Ezyclass" },
      { property: "og:description", content: "Cập nhật hồ sơ, mật khẩu và tuỳ chọn cá nhân cho tài khoản Ezyclass." },
      { property: "og:url", content: "https://ezyclass.com/settings" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/settings" }],
  }),
});


const SUBJECTS = ["Toán", "Văn", "Anh", "Lý", "Hóa", "Sinh", "Sử", "Địa", "Tin học", "Mỹ thuật", "Âm nhạc", "GDCD"];

function SubjectChips({
  value,
  onChange,
  max,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  max?: number;
}) {
  const [showCustom, setShowCustom] = React.useState(false);
  const [customInput, setCustomInput] = React.useState("");
  const atMax = max !== undefined && value.length >= max;
  const customValues = value.filter(v => !SUBJECTS.includes(v));

  const addCustom = () => {
    const t = customInput.trim();
    if (!t) return;
    if (value.includes(t)) { setCustomInput(""); setShowCustom(false); return; }
    if (atMax) return;
    onChange([...value, t]);
    setCustomInput("");
    setShowCustom(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {SUBJECTS.map((s) => {
          const selected = value.includes(s);
          const disabled = !selected && atMax;
          return (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (selected) onChange(value.filter((v) => v !== s));
                else onChange([...value, s]);
              }}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm border transition-all active:scale-[0.97]",
                selected
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:border-slate-700",
                disabled && "opacity-40 cursor-not-allowed",
              )}
            >
              {s}
            </button>
          );
        })}
        {customValues.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(value.filter((v) => v !== s))}
            className="px-3 py-1.5 rounded-full text-sm border bg-blue-600 text-white border-blue-600 transition-all active:scale-[0.97]"
            title="Bỏ chọn"
          >
            {s} ✕
          </button>
        ))}
        <button
          type="button"
          disabled={atMax}
          onClick={() => setShowCustom(true)}
          className={cn(
            "px-3 py-1.5 rounded-full text-sm border border-dashed transition-all",
            "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-slate-400",
            atMax && "opacity-40 cursor-not-allowed",
          )}
        >
          Khác...
        </button>
      </div>
      {showCustom && (
        <div className="flex gap-2">
          <Input
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder="Nhập tên môn..."
            autoFocus
            maxLength={40}
          />
          <Button type="button" size="sm" onClick={addCustom}>Thêm</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => { setShowCustom(false); setCustomInput(""); }}>Huỷ</Button>
        </div>
      )}
    </div>
  );
}

function passwordStrength(pw: string): { level: 0 | 1 | 2 | 3 | 4; label: string; color: string } {
  if (!pw) return { level: 0, label: "", color: "bg-slate-200 dark:bg-slate-800" };
  if (pw.length < 6) return { level: 1, label: "Yếu", color: "bg-red-500" };
  const hasNum = /\d/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  if (pw.length >= 8 && hasNum && hasSpecial) return { level: 4, label: "Rất mạnh", color: "bg-emerald-600" };
  if (pw.length >= 8 && hasNum) return { level: 3, label: "Mạnh", color: "bg-emerald-500" };
  return { level: 2, label: "Trung bình", color: "bg-amber-500" };
}

function SettingsPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();

  // Basic
  const [displayName, setDisplayName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [dateOfBirth, setDateOfBirth] = React.useState("");
  const [avatarKey, setAvatarKey] = React.useState<AvatarKey | null>(null);
  const [currentAvatarUrl, setCurrentAvatarUrl] = React.useState<string | null>(null);
  const [customAvatarFile, setCustomAvatarFile] = React.useState<File | null>(null);
  const [customAvatarPreview, setCustomAvatarPreview] = React.useState<string | null>(null);
  const [savingBasic, setSavingBasic] = React.useState(false);

  // Role-specific
  const [centerName, setCenterName] = React.useState("");
  const [centerAddress, setCenterAddress] = React.useState("");
  const [classroomName, setClassroomName] = React.useState("");
  const [teachingSubjects, setTeachingSubjects] = React.useState<string[]>([]);
  const [favSubjects, setFavSubjects] = React.useState<string[]>([]);
  const [schoolName, setSchoolName] = React.useState("");
  const [savingRole, setSavingRole] = React.useState(false);

  // Password
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [savingPw, setSavingPw] = React.useState(false);

  React.useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [loading, user, navigate]);

  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      supabase
        .from("profiles")
        .select("display_name, avatar_key, avatar_url, extra_info")
        .eq("id", user.id)
        .maybeSingle(),
      supabase.rpc("get_profile_private", { _user_id: user.id }),
    ])
      .then(([profileRes, privateRes]) => {
        if (cancelled) return;
        const data = profileRes.data;
        const priv = (privateRes.data ?? [])[0] as
          | { phone: string | null; date_of_birth: string | null }
          | undefined;
        if (!data) return;
        setDisplayName(data.display_name ?? "");
        setPhone(priv?.phone ?? "");
        setDateOfBirth(priv?.date_of_birth ?? "");
        setAvatarKey((data.avatar_key as AvatarKey) ?? null);
        setCurrentAvatarUrl(data.avatar_url ?? null);
        const extra = (data.extra_info as Record<string, unknown>) ?? {};
        if (user.role === "admin") {
          setCenterName((extra.centerName as string) ?? "");
          setCenterAddress((extra.centerAddress as string) ?? "");
        } else if (user.role === "teacher") {
          setClassroomName((extra.classroomName as string) ?? "");
          setTeachingSubjects((extra.teachingSubjects as string[]) ?? []);
        } else {
          setFavSubjects((extra.subjects as string[]) ?? []);
          setSchoolName((extra.schoolName as string) ?? "");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleCustomFile = (f: File) => {
    setCustomAvatarFile(f);
    setCustomAvatarPreview(URL.createObjectURL(f));
  };

  const previewAvatar = (() => {
    if (customAvatarPreview) return { type: "url" as const, value: customAvatarPreview };
    if (currentAvatarUrl && !customAvatarFile && avatarKey === null) return { type: "url" as const, value: currentAvatarUrl };
    const a = avatarByKey(avatarKey);
    if (a) return { type: "emoji" as const, emoji: a.emoji, bg: a.bg };
    if (currentAvatarUrl) return { type: "url" as const, value: currentAvatarUrl };
    return { type: "initials" as const, value: (displayName || user?.email || "?").slice(0, 2).toUpperCase() };
  })();

  const handleSaveBasic = async () => {
    if (!user) return;
    if (!displayName.trim()) {
      toast.error("Vui lòng nhập tên hiển thị");
      return;
    }
    setSavingBasic(true);
    try {
      let avatarUrl: string | null | undefined = undefined;
      if (customAvatarFile) {
        const ext = customAvatarFile.name.split(".").pop() || "png";
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("avatars")
          .upload(path, customAvatarFile, { upsert: true, contentType: customAvatarFile.type });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
        avatarUrl = pub.publicUrl;
      }
      await updateProfile({
        data: {
          displayName: displayName.trim(),
          phone: phone.trim() || null,
          dateOfBirth: dateOfBirth || null,
          avatarKey: customAvatarFile ? null : avatarKey ?? null,
          ...(avatarUrl !== undefined ? { avatarUrl } : {}),
        },
      });
      await refresh();
      if (avatarUrl) setCurrentAvatarUrl(avatarUrl);
      setCustomAvatarFile(null);
      setCustomAvatarPreview(null);
      toast.success("Đã lưu thông tin");
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Không lưu được");
    } finally {
      setSavingBasic(false);
    }
  };

  const handleSaveRole = async () => {
    if (!user) return;
    let extraInfo: Record<string, unknown>;
    if (user.role === "admin") {
      if (!centerName.trim()) { toast.error("Vui lòng nhập tên trung tâm"); return; }
      extraInfo = { centerName: centerName.trim(), centerAddress: centerAddress.trim() };
    } else if (user.role === "teacher") {
      if (!classroomName.trim()) { toast.error("Vui lòng nhập tên gọi trong lớp"); return; }
      extraInfo = { classroomName: classroomName.trim(), teachingSubjects };
    } else {
      extraInfo = { subjects: favSubjects, schoolName: schoolName.trim() };
    }
    setSavingRole(true);
    try {
      await updateProfile({ data: { extraInfo } });
      toast.success("Đã lưu thông tin");
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Không lưu được");
    } finally {
      setSavingRole(false);
    }
  };

  const handleSavePassword = async () => {
    if (newPassword.length < 6) { toast.error("Mật khẩu phải có ít nhất 6 ký tự"); return; }
    if (newPassword !== confirmPassword) { toast.error("Mật khẩu xác nhận không khớp"); return; }
    setSavingPw(true);
    try {
      await updatePassword({ data: { newPassword } });
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Đã cập nhật mật khẩu");
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Không cập nhật được");
    } finally {
      setSavingPw(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        Đang tải…
      </div>
    );
  }

  const strength = passwordStrength(newPassword);

  return (
    <PortalLayout portal={user.role} title="Cài đặt tài khoản" breadcrumb={[{ label: ROLE_HOME[user.role].slice(1), to: ROLE_HOME[user.role] }, { label: "Cài đặt" }]}>
      <div className="max-w-[720px] mx-auto space-y-6">
        {user.role === "admin" && <PlanInfoCard />}
        {/* Section 1: Avatar + basic info */}
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h2 className="font-display text-lg font-semibold mb-1">Thông tin cá nhân</h2>
          <p className="text-sm text-muted-foreground mb-5">Ảnh đại diện và thông tin cơ bản hiển thị trên toàn hệ thống.</p>

          <div className="flex items-center gap-4 mb-6">
            <div className={cn(
              "size-20 rounded-full grid place-items-center text-3xl shrink-0 overflow-hidden",
              previewAvatar.type === "emoji" ? previewAvatar.bg : "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium",
            )}>
              {previewAvatar.type === "url" ? (
                <img src={previewAvatar.value} alt="" className="w-full h-full object-cover" />
              ) : previewAvatar.type === "emoji" ? (
                <span aria-hidden>{previewAvatar.emoji}</span>
              ) : (
                <span className="text-xl">{previewAvatar.value}</span>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-display text-xl font-semibold truncate">{displayName || "Chưa đặt tên"}</div>
              <div className="text-sm text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>

          <div className="space-y-2 mb-5">
            <Label>Chọn avatar</Label>
            <AvatarPicker
              value={avatarKey}
              onChange={(k) => {
                setAvatarKey(k);
                setCustomAvatarFile(null);
                setCustomAvatarPreview(null);
              }}
              customUrl={customAvatarPreview}
              onCustomFile={handleCustomFile}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="displayName">Tên hiển thị *</Label>
              <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={80} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Số điện thoại</Label>
              <Input
                id="phone" type="tel" value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0901 234 567"
                readOnly={user.role !== "admin"}
                className={user.role !== "admin" ? "bg-muted/50 cursor-not-allowed" : ""}
              />
              {user.role !== "admin" && (
                <p className="text-xs text-muted-foreground">Liên hệ trung tâm để thay đổi thông tin này.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Ngày sinh</Label>
              <Input
                id="dob" type="date" value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                readOnly={user.role !== "admin"}
                className={user.role !== "admin" ? "bg-muted/50 cursor-not-allowed" : ""}
              />
              {user.role !== "admin" && (
                <p className="text-xs text-muted-foreground">Liên hệ trung tâm để thay đổi.</p>
              )}
            </div>

          </div>

          <div className="mt-5 flex justify-end">
            <Button onClick={handleSaveBasic} disabled={savingBasic} className="h-10">
              <Save className="size-4" /> {savingBasic ? "Đang lưu..." : "Lưu thông tin cơ bản"}
            </Button>
          </div>
        </motion.section>

        {/* Section 2: Role-specific */}
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h2 className="font-display text-lg font-semibold mb-1">
            {user.role === "admin" ? "Thông tin trung tâm" : user.role === "teacher" ? "Thông tin giảng dạy" : "Thông tin học tập"}
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            Chi tiết theo vai trò của bạn trên Ezyclass.
          </p>

          {user.role === "admin" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="centerName">Tên trung tâm *</Label>
                <Input id="centerName" value={centerName} onChange={(e) => setCenterName(e.target.value)} placeholder="VD: Trung tâm Anh ngữ Sunrise" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="centerAddress">Địa chỉ trung tâm</Label>
                <Input id="centerAddress" value={centerAddress} onChange={(e) => setCenterAddress(e.target.value)} placeholder="VD: 123 Lê Lợi, Q1, TP.HCM" />
              </div>
            </div>
          )}

          {user.role === "teacher" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="classroomName">Tên gọi trong lớp *</Label>
                <Input id="classroomName" value={classroomName} onChange={(e) => setClassroomName(e.target.value)} placeholder="VD: Thầy Minh / Cô Lan" />
              </div>
              <div className="space-y-2">
                <Label>Môn giảng dạy</Label>
                <SubjectChips value={teachingSubjects} onChange={setTeachingSubjects} />
              </div>
            </div>
          )}

          {user.role === "student" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Môn yêu thích <span className="text-xs text-muted-foreground">(tối đa 3)</span></Label>
                <SubjectChips value={favSubjects} onChange={setFavSubjects} max={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="schoolName">Trường đang học</Label>
                <Input id="schoolName" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="VD: THPT Lê Quý Đôn" />
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <Button onClick={handleSaveRole} disabled={savingRole} className="h-10">
              <Save className="size-4" /> {savingRole ? "Đang lưu..." : "Lưu"}
            </Button>
          </div>
        </motion.section>

        {/* Section 3: Password */}
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h2 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
            <Lock className="size-4" /> Đổi mật khẩu
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Đặt mật khẩu mới để bảo vệ tài khoản.</p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Mật khẩu mới</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label={showPw ? "Ẩn" : "Hiện"}
                >
                  {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {newPassword && (
                <div className="space-y-1.5 pt-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={cn("h-1.5 flex-1 rounded-full transition-colors", i <= strength.level ? strength.color : "bg-slate-200 dark:bg-slate-800")}
                      />
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground">Độ mạnh: <span className="font-medium text-foreground">{strength.label}</span></div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Xác nhận mật khẩu mới</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label={showConfirm ? "Ẩn" : "Hiện"}
                >
                  {showConfirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <Button onClick={handleSavePassword} disabled={savingPw || !newPassword || !confirmPassword} className="h-10">
              <Lock className="size-4" /> {savingPw ? "Đang cập nhật..." : "Cập nhật mật khẩu"}
            </Button>
          </div>
        </motion.section>
      </div>
    </PortalLayout>
  );
}

const PLAN_LABELS: Record<string, string> = {
  free: "Miễn phí", tutor: "Gia sư", center: "Trung tâm",
  school: "Trường học", enterprise: "Enterprise",
};

function PlanInfoCard() {
  const ent = useEntitlements();
  const isLimited = ent.tier === "free" || ent.tier === "tutor";
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border rounded-2xl p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-semibold text-sm text-muted-foreground">Gói đang dùng</h3>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg font-semibold">{PLAN_LABELS[ent.tier] ?? "Miễn phí"}</span>
            {isLimited && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">Giới hạn</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {ent.currentClasses}/{ent.maxClasses === -1 ? "∞" : ent.maxClasses} lớp · {ent.currentStudents}/{ent.maxStudents === -1 ? "∞" : ent.maxStudents} học viên
          </div>
        </div>
        <Link to="/billing">
          <Button variant="outline" size="sm">
            {isLimited ? "Nâng cấp" : "Quản lý gói"}
          </Button>
        </Link>
      </div>
    </motion.section>
  );
}
